import React from 'react';
import classNames from "classnames";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import i18n from '@breward/i18n';

interface MenuItem {
    key: string;
    icon: string;
    label?: string;
    badge?: boolean;
}

interface GenericSideMenuProps {
    items: MenuItem[];
    basePath?: string;
    onLogout?: () => void;
    showLabels?: boolean;
}

const SideMenu: React.FC<GenericSideMenuProps> = ({
    items,
    basePath = '/app',
    onLogout,
    showLabels = false,
}) => {
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();

    const currentItemKey = location.pathname.match(
        new RegExp(`${basePath}/${params?.id ?? ''}/?(.*)`)
    )?.[1];

    const handleClick = (key: string) => {
        navigate(key);
    };

    return (
        <div
            className="z-20 flex h-full w-14 flex-col gap-2 border-r border-base-200 bg-gradient-to-b from-base-100 to-base-200 p-2 shadow-xl backdrop-blur-sm dark:border-primary-800/50 dark:from-content-900 dark:to-content-950 dark:shadow-content-950/50 sm:w-16 sm:p-2"
        >
            <div className="flex flex-1 flex-col gap-4">
                {items.map((menuItem) => (
                    <div
                        key={menuItem.key}
                        onClick={() => handleClick(menuItem.key)}
                        className={classNames(
                            "relative flex h-10 w-10 sm:h-12 sm:w-12 mx-auto cursor-pointer flex-col items-center justify-center rounded-xl transition-all duration-300 hover:bg-black/5 dark:hover:bg-white/10 hover:scale-105",
                            currentItemKey?.match(new RegExp(`${menuItem.key}.*`)) &&
                            "bg-base-300/30 dark:bg-primary-500/15"
                        )}
                    >
                        <div
                            className={classNames(
                                "transition-all duration-300",
                                currentItemKey?.match(new RegExp(`${menuItem.key}.*`))
                                    ? "text-primary-500 dark:text-primary-400 scale-110"
                                    : "text-content-500 dark:text-content-400",
                                menuItem.icon,
                                "h-5 w-5 sm:h-6 sm:w-6"
                            )}
                        />
                        {showLabels && (
                            <div className={classNames(
                                "mt-1 text-[0.8rem] font-sans font-medium transition-colors duration-300",
                                currentItemKey?.match(new RegExp(`${menuItem.key}.*`))
                                    ? "text-primary-500 dark:text-primary-400"
                                    : "text-content-500 dark:text-content-400"
                            )}>
                                {menuItem.label}
                            </div>
                        )}
                        {currentItemKey?.match(new RegExp(`${menuItem.key}.*`)) && (
                            <div
                                className="absolute -left-2 sm:-left-3 h-full w-1 rounded-full bg-gradient-to-b from-primary-400 to-primary-600"
                            />
                        )}
                        {menuItem.badge && (
                            <div className="absolute top-1 right-1 sm:top-2 sm:right-2 h-2.5 w-2.5 rounded-full bg-red-500 shadow-sm border border-white dark:border-content-700" />
                        )}
                    </div>
                ))}
            </div>
            {onLogout && (
                <div
                    onClick={() => onLogout()}
                    className="mb-5 flex h-10 w-10 sm:h-12 sm:w-12 mx-auto cursor-pointer flex-col items-center justify-center rounded-xl opacity-70 transition-all duration-300 hover:opacity-100 hover:scale-105 hover:bg-red-500/20"
                >
                    <div className="icon-[ph--sign-out] h-5 w-5 sm:h-6 sm:w-6 text-red-500 transition-transform duration-300 hover:rotate-12" />
                    {showLabels && (
                        <div className="mt-1 text-xs font-sans font-medium text-red-500">
                            {i18n.t('global:shared.logout', "Logout")}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SideMenu;
